import { linkifyPhones, safeUrl } from '@itin/shared'
import type { Root, RootContent } from 'mdast'
import ReactMarkdown from 'react-markdown'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import { visit } from 'unist-util-visit'

/**
 * The only place user-supplied text becomes markup.
 *
 * Three rules, all of them load-bearing:
 *
 *  1. **No `rehype-raw`.** It is not installed and must not be. Raw HTML
 *     rendering is how products like this one get cross-site scripting, and no
 *     feature is worth reopening it.
 *  2. **Every href goes through `safeUrl`**, which strips the characters a URL
 *     parser ignores *before* reading the scheme. Checking the raw string and
 *     rendering the raw string is the bypass this closes.
 *  3. **Phone linkification is deliberately conservative.** A previous build
 *     turned the insurance policy number `4471-9920` into a dialable link. The
 *     rule now lives in `@itin/shared` with that exact case as a test.
 */

/**
 * Turns dialable numbers in prose into `tel:` links.
 *
 * Skips text that is already inside a link, so a Markdown link whose label
 * happens to contain digits is left alone rather than nested.
 */
function remarkPhoneNumbers() {
  return (tree: Root): void => {
    visit(tree, 'text', (node, index, parent) => {
      if (!parent || index === undefined) return
      if (parent.type === 'link' || parent.type === 'linkReference') return

      const segments = linkifyPhones(node.value)
      if (segments.length === 1 && segments[0]!.type === 'text') return

      const replacement: RootContent[] = segments.map((segment) =>
        segment.type === 'text'
          ? { type: 'text', value: segment.value }
          : {
              type: 'link',
              url: segment.href,
              children: [{ type: 'text', value: segment.value }],
            },
      )

      parent.children.splice(index, 1, ...replacement)
      return index + replacement.length
    })
  }
}

export function Markdown({ children }: { children: string | null | undefined }) {
  if (!children?.trim()) return null

  return (
    <div className="prose-plan text-sm leading-relaxed [&_a]:underline [&_li]:my-0.5 [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-5">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks, remarkPhoneNumbers]}
        // Anything that fails the allowlist becomes an empty href rather than a
        // rendered attacker-controlled scheme.
        urlTransform={(url) => safeUrl(url) ?? ''}
        components={{
          a: ({ href, children: label }) => {
            const safe = safeUrl(href)
            // No href at all rather than a dead link: a traveller tapping
            // something that silently does nothing is confusing, and plain text
            // is honest about what it is.
            if (!safe) return <span>{label}</span>
            const external = safe.startsWith('http')
            return (
              <a
                href={safe}
                {...(external
                  ? { target: '_blank', rel: 'noopener noreferrer nofollow' }
                  : {})}
              >
                {label}
              </a>
            )
          },
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  )
}
